import { clsx } from 'clsx'
import { ChevronRight } from 'lucide-react'
import { useSurvivorAdminGameweeks, type SurvivorAdminGameweek } from '../queries'
import styles from './GameweekList.module.css'

type GameweekListProps = {
  competitionId:     string | null
  selectedId:        string | null
  onSelect:          (gameweekId: string) => void
}

export function GameweekList({ competitionId, selectedId, onSelect }: GameweekListProps) {
  const { data: gameweeks, isPending, isError, refetch } = useSurvivorAdminGameweeks(competitionId)

  if (!competitionId) {
    return <div className={styles.empty}>Selecione uma competição.</div>
  }
  if (isPending) {
    return <div className={styles.empty}>Carregando rodadas…</div>
  }
  if (isError) {
    return (
      <div className={styles.empty}>
        Não foi possível carregar as rodadas.{' '}
        <button type="button" className={styles.retry} onClick={() => void refetch()}>
          Tentar novamente
        </button>
      </div>
    )
  }
  if (gameweeks.length === 0) {
    return (
      <div className={styles.empty}>
        Nenhuma rodada ainda — importe o calendário da competição.
      </div>
    )
  }

  return (
    <ul className={styles.list}>
      {gameweeks.map((gw) => (
        <li key={gw.id}>
          <button
            type="button"
            onClick={() => onSelect(gw.id)}
            className={clsx(styles.row, gw.id === selectedId && styles.rowSelected)}
          >
            <span className={styles.number}>R{gw.number}</span>
            <span className={styles.info}>
              <span className={styles.dates}>{formatRange(gw)}</span>
              <span className={styles.phase}>{gw.phase}</span>
            </span>
            <span className={clsx(styles.status, styles[`status_${gw.status}`])}>
              {STATUS_LABEL[gw.status]}
            </span>
            <ChevronRight size={14} className={styles.chevron} />
          </button>
        </li>
      ))}
    </ul>
  )
}

const STATUS_LABEL: Record<SurvivorAdminGameweek['status'], string> = {
  UPCOMING:  'A abrir',
  ACTIVE:    'Aberta',
  LOCKED:    'Fechada',
  FINALIZED: 'Finalizada',
}

function formatRange(gw: SurvivorAdminGameweek): string {
  const start = new Date(gw.firstMatchAt)
  const end   = new Date(gw.lastMatchEndsAt)
  const fmt = (d: Date) =>
    d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  return start.toDateString() === end.toDateString()
    ? fmt(start)
    : `${fmt(start)} – ${fmt(end)}`
}
